import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Car,
  Check,
  Copy,
  DollarSign,
  ExternalLink,
  Gauge,
  Plus,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import * as React from "react";
import { useSearchParams } from "react-router-dom";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Card, CardContent } from "@/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/dialog";
import { type GalleryImage, GalleryUpload } from "@/components/gallery-upload";
import { Input } from "@/components/input";
import { Label } from "@/components/label";
import { Layout } from "@/components/layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { Textarea } from "@/components/textarea";
import { formatCurrency, type Vehicle, type VehicleStatus } from "@/lib/types";

interface VehicleFormData {
  make: string;
  model: string;
  year: string;
  vin: string;
  mileage: string;
  price: string;
  color: string;
  status: VehicleStatus;
  description: string;
  images: GalleryImage[];
}

const emptyVehicle: VehicleFormData = {
  make: "",
  model: "",
  year: new Date().getFullYear().toString(),
  vin: "",
  mileage: "",
  price: "",
  color: "",
  status: "available" as VehicleStatus,
  description: "",
  images: [],
};

export function Inventory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [search, setSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<string>("all");
  const [showFilters, setShowFilters] = React.useState(false);

  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(searchParams.get("new") === "true");
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [selectedVehicle, setSelectedVehicle] = React.useState<Vehicle | null>(null);
  const [formData, setFormData] = React.useState(emptyVehicle);
  const [copiedId, setCopiedId] = React.useState<number | null>(null);

  // Helper function to get public listing URL
  const getListingUrl = (vehicleId: number) => {
    return `${window.location.origin}/listing/${vehicleId}`;
  };

  // Copy link to clipboard
  const copyListingLink = async (vehicleId: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await navigator.clipboard.writeText(getListingUrl(vehicleId));
      setCopiedId(vehicleId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  // Fetch vehicles
  const { data: vehicles = [], isLoading } = useQuery<Vehicle[]>({
    queryKey: ["vehicles", statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      const res = await fetch(`/api/vehicles?${params}`);
      if (!res.ok) throw new Error("Failed to fetch vehicles");
      return res.json();
    },
  });

  // Create vehicle mutation
  const createVehicle = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await fetch("/api/vehicles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          year: parseInt(data.year),
          mileage: data.mileage ? parseInt(data.mileage) : null,
          price: parseInt(data.price),
        }),
      });
      if (!res.ok) throw new Error("Failed to create vehicle");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      setIsAddDialogOpen(false);
      setFormData(emptyVehicle);
      setSearchParams({});
    },
  });

  // Update vehicle mutation
  const updateVehicle = useMutation({
    mutationFn: async (data: typeof formData & { id: number }) => {
      const res = await fetch(`/api/vehicles/${data.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          year: parseInt(data.year),
          mileage: data.mileage ? parseInt(data.mileage as string) : null,
          price: parseInt(data.price as string),
        }),
      });
      if (!res.ok) throw new Error("Failed to update vehicle");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      setIsEditDialogOpen(false);
      setSelectedVehicle(null);
    },
  });

  // Filter vehicles by search
  const filteredVehicles = vehicles.filter(
    (vehicle) =>
      vehicle.make.toLowerCase().includes(search.toLowerCase()) ||
      vehicle.model.toLowerCase().includes(search.toLowerCase()) ||
      vehicle.vin?.toLowerCase().includes(search.toLowerCase()) ||
      vehicle.year.toString().includes(search)
  );

  // Calculate inventory stats
  const totalValue = vehicles
    .filter((v) => v.status === "available")
    .reduce((sum, v) => sum + v.price, 0);
  const availableCount = vehicles.filter((v) => v.status === "available").length;
  const soldCount = vehicles.filter((v) => v.status === "sold").length;

  const handleEdit = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    // Convert vehicle images to gallery format
    const galleryImages: GalleryImage[] =
      vehicle.images?.map((img) => ({
        id: img.id,
        url: img.image_url,
        sort_order: img.sort_order,
        is_primary: img.is_primary,
      })) || [];

    setFormData({
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year.toString(),
      vin: vehicle.vin || "",
      mileage: vehicle.mileage?.toString() || "",
      price: vehicle.price.toString(),
      color: vehicle.color || "",
      status: vehicle.status,
      description: vehicle.description || "",
      images: galleryImages,
    });
    setIsEditDialogOpen(true);
  };

  const getStatusBadgeVariant = (
    status: VehicleStatus
  ): "available" | "sold" | "reserved" | "pending" => {
    return status;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">รถยนต์ในสต็อก</h1>
            <p className="mt-1 text-slate-500">จัดการรถยนต์มือสอง</p>
          </div>
          <Button
            className="rounded-xl bg-indigo-500 hover:bg-indigo-600"
            onClick={() => {
              setFormData(emptyVehicle);
              setIsAddDialogOpen(true);
            }}
          >
            <Plus className="mr-2 size-4" />
            เพิ่มรถยนต์
          </Button>
        </div>

        {/* Inventory Stats */}
        <div className="grid gap-4 sm:grid-cols-3">
          <Card className="rounded-2xl border-slate-200 shadow-sm">
            <CardContent className="flex items-center gap-4 p-5">
              <div className="flex size-12 items-center justify-center rounded-xl bg-indigo-50">
                <Car className="size-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">พร้อมขาย</p>
                <p className="text-2xl font-bold text-indigo-600">{availableCount}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-200 shadow-sm">
            <CardContent className="flex items-center gap-4 p-5">
              <div className="flex size-12 items-center justify-center rounded-xl bg-emerald-50">
                <DollarSign className="size-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">มูลค่าสต็อก</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalValue)}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-200 shadow-sm">
            <CardContent className="flex items-center gap-4 p-5">
              <div className="flex size-12 items-center justify-center rounded-xl bg-amber-50">
                <Car className="size-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">ขายเดือนนี้</p>
                <p className="text-2xl font-bold text-amber-600">{soldCount}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="ค้นหาด้วยยี่ห้อ, รุ่น, ปี, หรือ VIN..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-xl border-slate-200 pl-9"
            />
          </div>
          <Button
            variant="outline"
            className="rounded-xl border-slate-200"
            onClick={() => setShowFilters(!showFilters)}
          >
            <SlidersHorizontal className="mr-2 size-4" />
            ตัวกรอง
          </Button>
        </div>

        {/* Filters */}
        {showFilters && (
          <Card className="rounded-2xl border-slate-200">
            <CardContent className="flex flex-wrap gap-4 p-4">
              <div className="min-w-[150px]">
                <Label className="mb-2 block text-xs text-slate-500">สถานะ</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="rounded-xl border-slate-200">
                    <SelectValue placeholder="ทุกสถานะ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ทุกสถานะ</SelectItem>
                    <SelectItem value="available">พร้อมขาย</SelectItem>
                    <SelectItem value="sold">ขายแล้ว</SelectItem>
                    <SelectItem value="reserved">จอง</SelectItem>
                    <SelectItem value="pending">รอดำเนินการ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Vehicles Grid */}
        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse overflow-hidden rounded-2xl border-slate-200">
                <div className="aspect-video bg-slate-100" />
                <CardContent className="space-y-3 p-4">
                  <div className="h-5 w-32 rounded-lg bg-slate-100" />
                  <div className="h-4 w-24 rounded-lg bg-slate-100" />
                  <div className="h-6 w-20 rounded-lg bg-slate-100" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredVehicles.length === 0 ? (
          <Card className="rounded-2xl border-slate-200">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <div className="flex size-14 items-center justify-center rounded-2xl bg-slate-100">
                <Car className="size-7 text-slate-400" />
              </div>
              <p className="mt-3 text-sm font-medium text-slate-600">ไม่พบรถยนต์</p>
              <p className="text-xs text-slate-400">
                {search ? "ลองปรับการค้นหาใหม่" : "เพิ่มรถยนต์คันแรกเพื่อเริ่มต้น"}
              </p>
              {!search && (
                <Button
                  className="mt-4 rounded-xl bg-indigo-500 hover:bg-indigo-600"
                  size="sm"
                  onClick={() => {
                    setFormData(emptyVehicle);
                    setIsAddDialogOpen(true);
                  }}
                >
                  <Plus className="mr-2 size-4" />
                  เพิ่มรถยนต์
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredVehicles.map((vehicle) => (
              <Card
                key={vehicle.id}
                className="cursor-pointer overflow-hidden rounded-2xl border-slate-200 shadow-sm transition-all hover:border-indigo-200 hover:shadow-lg"
                onClick={() => handleEdit(vehicle)}
              >
                <div className="relative aspect-video bg-slate-100">
                  {vehicle.image_url ? (
                    <img
                      src={vehicle.image_url}
                      alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <Car className="size-16 text-slate-300" />
                    </div>
                  )}
                  <Badge
                    variant={getStatusBadgeVariant(vehicle.status)}
                    className="absolute right-3 top-3"
                  >
                    {vehicle.status === "available"
                      ? "พร้อมขาย"
                      : vehicle.status === "sold"
                        ? "ขายแล้ว"
                        : vehicle.status === "reserved"
                          ? "จอง"
                          : "รอดำเนินการ"}
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <h3 className="text-lg font-bold text-slate-900">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                    {vehicle.mileage && (
                      <span className="flex items-center gap-1">
                        <Gauge className="size-3.5" />
                        {vehicle.mileage.toLocaleString("th-TH")} กม.
                      </span>
                    )}
                    {vehicle.color && <span>{vehicle.color}</span>}
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <p className="text-xl font-bold text-indigo-600">
                      {formatCurrency(vehicle.price)}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1.5 rounded-lg text-xs text-slate-500 hover:text-indigo-600"
                      onClick={(e) => copyListingLink(vehicle.id, e)}
                    >
                      {copiedId === vehicle.id ? (
                        <>
                          <Check className="size-3.5 text-emerald-500" />
                          คัดลอกแล้ว
                        </>
                      ) : (
                        <>
                          <Copy className="size-3.5" />
                          Share
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Vehicle Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onOpenChange={(open) => {
          setIsAddDialogOpen(open);
          if (!open) setSearchParams({});
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>เพิ่มรถยนต์ใหม่</DialogTitle>
            <DialogDescription>เพิ่มรถยนต์เข้าสต็อก</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createVehicle.mutate(formData);
            }}
            className="space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="make">ยี่ห้อ *</Label>
                <Input
                  id="make"
                  placeholder="Toyota"
                  className="rounded-xl border-slate-200"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="model">รุ่น *</Label>
                <Input
                  id="model"
                  placeholder="Camry"
                  className="rounded-xl border-slate-200"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="year">ปี *</Label>
                <Input
                  id="year"
                  type="number"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  className="rounded-xl border-slate-200"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="price">ราคา *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">฿</span>
                  <Input
                    id="price"
                    type="number"
                    placeholder="850000"
                    className="rounded-xl border-slate-200 pl-8"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="mileage">เลขไมล์</Label>
                <Input
                  id="mileage"
                  type="number"
                  placeholder="45000"
                  className="rounded-xl border-slate-200"
                  value={formData.mileage}
                  onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="color">สี</Label>
                <Input
                  id="color"
                  placeholder="เงิน"
                  className="rounded-xl border-slate-200"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="vin">VIN</Label>
                <Input
                  id="vin"
                  placeholder="1HGBH41JXMN109186"
                  maxLength={17}
                  className="rounded-xl border-slate-200"
                  value={formData.vin}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      vin: e.target.value.toUpperCase(),
                    })
                  }
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="status">สถานะ</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: VehicleStatus) =>
                    setFormData({ ...formData, status: value })
                  }
                >
                  <SelectTrigger className="rounded-xl border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">พร้อมขาย</SelectItem>
                    <SelectItem value="reserved">จอง</SelectItem>
                    <SelectItem value="pending">รอดำเนินการ</SelectItem>
                    <SelectItem value="sold">ขายแล้ว</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>รูปถ่ายรถ</Label>
                <GalleryUpload
                  images={formData.images}
                  onChange={(images) => setFormData({ ...formData, images })}
                  maxImages={10}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="description">รายละเอียด</Label>
                <Textarea
                  id="description"
                  placeholder="รายละเอียดรถยนต์..."
                  className="rounded-xl border-slate-200"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                onClick={() => {
                  setIsAddDialogOpen(false);
                  setSearchParams({});
                }}
              >
                ยกเลิก
              </Button>
              <Button
                type="submit"
                className="rounded-xl bg-indigo-500 hover:bg-indigo-600"
                disabled={createVehicle.isPending}
              >
                {createVehicle.isPending ? "กำลังเพิ่ม..." : "เพิ่มรถยนต์"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Vehicle Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>แก้ไขรถยนต์</DialogTitle>
            <DialogDescription>อัพเดทข้อมูลรถยนต์</DialogDescription>
          </DialogHeader>

          {/* Share Listing Section */}
          {selectedVehicle && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <Label className="mb-2 block text-sm font-medium">แชร์หน้าประกาศ</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={getListingUrl(selectedVehicle.id)}
                  className="flex-1 rounded-lg bg-white text-sm"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="flex-shrink-0 rounded-lg"
                  onClick={() => copyListingLink(selectedVehicle.id)}
                >
                  {copiedId === selectedVehicle.id ? (
                    <>
                      <Check className="mr-1.5 h-4 w-4 text-emerald-500" />
                      คัดลอกแล้ว
                    </>
                  ) : (
                    <>
                      <Copy className="mr-1.5 h-4 w-4" />
                      คัดลอก
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0 rounded-lg"
                  onClick={() => window.open(getListingUrl(selectedVehicle.id), "_blank")}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (selectedVehicle) {
                updateVehicle.mutate({ ...formData, id: selectedVehicle.id });
              }
            }}
            className="space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="edit-make">ยี่ห้อ *</Label>
                <Input
                  id="edit-make"
                  className="rounded-xl border-slate-200"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-model">รุ่น *</Label>
                <Input
                  id="edit-model"
                  className="rounded-xl border-slate-200"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-year">ปี *</Label>
                <Input
                  id="edit-year"
                  type="number"
                  className="rounded-xl border-slate-200"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-price">ราคา *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">฿</span>
                  <Input
                    id="edit-price"
                    type="number"
                    className="rounded-xl border-slate-200 pl-8"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="edit-mileage">เลขไมล์</Label>
                <Input
                  id="edit-mileage"
                  type="number"
                  className="rounded-xl border-slate-200"
                  value={formData.mileage}
                  onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-color">สี</Label>
                <Input
                  id="edit-color"
                  className="rounded-xl border-slate-200"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="edit-vin">VIN</Label>
                <Input
                  id="edit-vin"
                  maxLength={17}
                  className="rounded-xl border-slate-200"
                  value={formData.vin}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      vin: e.target.value.toUpperCase(),
                    })
                  }
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="edit-status">สถานะ</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: VehicleStatus) =>
                    setFormData({ ...formData, status: value })
                  }
                >
                  <SelectTrigger className="rounded-xl border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">พร้อมขาย</SelectItem>
                    <SelectItem value="reserved">จอง</SelectItem>
                    <SelectItem value="pending">รอดำเนินการ</SelectItem>
                    <SelectItem value="sold">ขายแล้ว</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>รูปถ่ายรถ</Label>
                <GalleryUpload
                  images={formData.images}
                  onChange={(images) => setFormData({ ...formData, images })}
                  maxImages={10}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="edit-description">รายละเอียด</Label>
                <Textarea
                  id="edit-description"
                  className="rounded-xl border-slate-200"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                onClick={() => setIsEditDialogOpen(false)}
              >
                ยกเลิก
              </Button>
              <Button
                type="submit"
                className="rounded-xl bg-indigo-500 hover:bg-indigo-600"
                disabled={updateVehicle.isPending}
              >
                {updateVehicle.isPending ? "กำลังบันทึก..." : "บันทึก"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
